var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { OperationRead } from '@galigeo-store/galigeojs';
import './BasicStatus.js';
import '../icon/Icon.js';
/** Per-type color for the `<basic-status>` badge. Transfer uses a custom
 *  hex (no named variant maps onto its purple shade). */
const OP_TYPE_COLOR = {
    [OperationRead.type.ADD]: 'success',
    [OperationRead.type.EDIT]: 'warning',
    [OperationRead.type.TRANSFER]: '#6e44a3',
    [OperationRead.type.DELETE]: 'danger'
};
const OP_TYPE_ICON = {
    [OperationRead.type.ADD]: 'plus',
    [OperationRead.type.EDIT]: 'edit',
    [OperationRead.type.TRANSFER]: 'swap',
    [OperationRead.type.DELETE]: 'delete'
};
function opTypeLabel(type) {
    switch (type) {
        case OperationRead.type.ADD:
            return msg('Add', {
                id: 'operationCard.opType.add',
                desc: 'Op-card type badge: add (verb)'
            });
        case OperationRead.type.EDIT:
            return msg('Modify', {
                id: 'operationCard.opType.edit',
                desc: 'Op-card type badge: modify (verb)'
            });
        case OperationRead.type.TRANSFER:
            return msg('Transfer', {
                id: 'operationCard.opType.transfer',
                desc: 'Op-card type badge: transfer (verb)'
            });
        case OperationRead.type.DELETE:
            return msg('Remove', {
                id: 'operationCard.opType.delete',
                desc: 'Op-card type badge: remove (verb)'
            });
    }
}
function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
let OperationCard = class OperationCard extends LitElement {
    constructor() {
        super(...arguments);
        this.name = '';
        this.props = [];
        this.hideImpact = false;
        this.hideActions = false;
        this.disabled = false;
        this.selected = false;
        this._menuOpen = false;
        this._onDocClick = (e) => {
            if (!this._menuOpen)
                return;
            const path = e.composedPath();
            if (path.includes(this))
                return;
            this._menuOpen = false;
        };
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this._onDocClick, true);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocClick, true);
    }
    updated(changed) {
        super.updated(changed);
        // Mirror the open-state to a host class so consuming stacking contexts
        // can lift this card above later siblings — :host() can't combine
        // with @state, so we reflect it manually.
        if (changed.has('_menuOpen') || changed.size === 0) {
            this.classList.toggle('is-menu-open', this._menuOpen);
        }
    }
    _emitClick() {
        if (this.disabled)
            return;
        this.dispatchEvent(new CustomEvent('card-click', {
            detail: { change: this.change },
            bubbles: true,
            composed: true
        }));
    }
    _toggleMenu(e) {
        e.stopPropagation();
        this._menuOpen = !this._menuOpen;
    }
    _emitEdit(e) {
        e.stopPropagation();
        this._menuOpen = false;
        this.dispatchEvent(new CustomEvent('operation-edit', {
            detail: { change: this.change },
            bubbles: true,
            composed: true
        }));
    }
    _emitDelete(e) {
        e.stopPropagation();
        this._menuOpen = false;
        this.dispatchEvent(new CustomEvent('operation-delete', {
            detail: { change: this.change },
            bubbles: true,
            composed: true
        }));
    }
    _renderLogo(net, extraTitle) {
        const title = extraTitle ? `${extraTitle}: ${net.name}` : net.name;
        return html `
      <span class="logo" title=${title}>
        ${net.logoUrl
            ? html `<img src=${net.logoUrl} alt="" />`
            : html `<span>${initials(net.name)}</span>`}
      </span>
    `;
    }
    _renderLogos() {
        const isTransfer = this.change?.type === OperationRead.type.TRANSFER;
        if (isTransfer && this.sourceNetwork && this.network) {
            const sourceLabel = msg('From', {
                id: 'operationCard.transfer.from',
                desc: 'Tooltip label preceding the source network of a transfer'
            });
            const destLabel = msg('To', {
                id: 'operationCard.transfer.to',
                desc: 'Tooltip label preceding the destination network of a transfer'
            });
            return html `
        <span
          class="logos pair"
          aria-label=${msg('Transfer between two networks', {
                id: 'operationCard.transfer.ariaLabel',
                desc: 'Accessible label for the paired-logos display on a transfer card'
            })}
        >
          ${this._renderLogo(this.sourceNetwork, sourceLabel)}
          ${this._renderLogo(this.network, destLabel)}
        </span>
      `;
        }
        if (this.network) {
            return html `
        <span class="logos">${this._renderLogo(this.network)}</span>
      `;
        }
        return '';
    }
    _renderMenu() {
        const isDelete = this.change?.type === OperationRead.type.DELETE;
        return html `
      <div class="menu-wrap">
        <button
          type="button"
          class="menu-btn"
          aria-haspopup="menu"
          aria-expanded=${this._menuOpen}
          title=${msg('Options', {
            id: 'operationCard.menu.title',
            desc: 'Tooltip on the kebab menu of an editable operation card'
        })}
          @click=${this._toggleMenu}
        >
          <basic-icon name="kebab" size="14"></basic-icon>
        </button>
        <div class="menu ${this._menuOpen ? 'open' : ''}" role="menu">
          ${isDelete
            ? ''
            : html `
                <button
                  type="button"
                  class="menu-item"
                  role="menuitem"
                  @click=${this._emitEdit}
                >
                  <basic-icon name="edit" size="14"></basic-icon>
                  ${msg('Edit', {
                id: 'operationCard.menu.edit',
                desc: 'Operation card kebab menu — edit'
            })}
                </button>
                <div class="menu-divider"></div>
              `}
          <button
            type="button"
            class="menu-item destructive"
            role="menuitem"
            @click=${this._emitDelete}
          >
            <basic-icon name="delete" size="14"></basic-icon>
            ${msg('Delete', {
            id: 'operationCard.menu.delete',
            desc: 'Operation card kebab menu — delete'
        })}
          </button>
        </div>
      </div>
    `;
    }
    render() {
        if (!this.change)
            return html ``;
        const showProps = this.props.length > 0;
        const showImpact = !this.hideImpact;
        return html `
      <div
        class="card"
        role="button"
        tabindex=${this.disabled ? -1 : 0}
        aria-disabled=${this.disabled}
        @click=${this._emitClick}
      >
        <div class="head">
          ${this._renderLogos()}
          <div class="info">
            <div class="name">${this.name}</div>
            ${this.subtitle
            ? html `<div class="subtitle">${this.subtitle}</div>`
            : ''}
          </div>
          <basic-status
            class="type-badge"
            .variant=${OP_TYPE_COLOR[this.change.type]}
            .status=${opTypeLabel(this.change.type)}
            .icon=${OP_TYPE_ICON[this.change.type]}
            size="small"
          ></basic-status>
          ${this.hideActions ? '' : this._renderMenu()}
        </div>

        ${showProps
            ? html `
              <div class="props">
                <span class="props-label"
                  >${msg('Properties', {
                id: 'operationCard.props.label',
                desc: 'Label preceding the property tags on an operation card'
            })}:</span
                >
                ${this.props.map((p) => html `<span class="prop-tag"
                    >${p.label}:<strong>${p.value}</strong></span
                  >`)}
              </div>
            `
            : ''}

        ${showImpact
            ? html `
              <div class="impact ${this.impactText ? '' : 'placeholder'}">
                <span class="impact-icon"><basic-icon name="chart-line" size="13"></basic-icon></span>
                <span>
                  ${this.impactText ??
                msg('Forecasted revenue impact: coming soon', {
                    id: 'operationCard.impact.placeholder',
                    desc: 'Placeholder text for the per-op revenue impact line'
                })}
                </span>
              </div>
            `
            : ''}
      </div>
    `;
    }
};
OperationCard.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .card {
      position: relative;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px 14px;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .card:hover {
      border-color: #0078d4;
      box-shadow: 0 2px 8px rgba(0, 120, 212, 0.08);
    }
    :host([disabled]) .card {
      cursor: default;
    }
    :host([disabled]) .card:hover {
      border-color: #e5e7eb;
      box-shadow: none;
    }
    :host([selected]) .card {
      border-color: #0078d4;
      box-shadow: 0 0 0 2px rgba(0, 120, 212, 0.18);
    }
    /* Lift the card whose kebab menu is open above its later siblings —
       otherwise the absolutely-positioned menu (constrained to this
       card's stacking context) renders behind the next card. */
    :host(.is-menu-open) {
      z-index: 10;
      position: relative;
    }

    /* ── Head row: logo(s) + name/subtitle + type badge ───── */
    .head {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 8px;
    }

    .info {
      flex: 1;
      min-width: 0;
    }
    .name {
      font-size: 13.5px;
      font-weight: 600;
      color: #1a2433;
      margin-bottom: 2px;
      word-break: break-word;
    }
    .subtitle {
      font-size: 11.5px;
      color: #7a8594;
      line-height: 1.35;
    }

    /* Type badge — rendered via <basic-status>; we just position it. */
    .type-badge {
      flex-shrink: 0;
      align-self: center;
    }

    /* ── Network logo(s) ──────────────────────────────────── */
    .logos {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
    }
    .logos.pair {
      /* Inset the second logo so the two discs overlap by 10px. */
      padding-right: 0;
    }
    .logos.pair .logo + .logo {
      margin-left: -10px;
    }
    .logo {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #f3f4f6;
      border: 2px solid #fff;
      box-shadow: 0 0 0 1px #e5e7eb;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      color: #6a7585;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      flex-shrink: 0;
    }
    .logo img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    /* ── Properties row ───────────────────────────────────── */
    .props {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;
    }
    .props-label {
      font-size: 11px;
      color: #98a3b3;
      align-self: center;
      margin-right: 2px;
    }
    .prop-tag {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      font-size: 11px;
      color: #4a5868;
      background: #f3f4f6;
      border-radius: 4px;
      white-space: nowrap;
    }
    .prop-tag strong {
      margin-left: 3px;
      color: #1a2433;
      font-weight: 600;
    }

    /* ── Impact footer ────────────────────────────────────── */
    .impact {
      display: flex;
      align-items: center;
      gap: 6px;
      padding-top: 8px;
      border-top: 1px solid #f0f0f0;
      font-size: 11.5px;
      color: #6a7585;
    }
    .impact-icon {
      display: inline-flex;
      flex-shrink: 0;
      color: inherit;
    }
    .impact.placeholder {
      color: #98a3b3;
      font-style: italic;
    }

    /* ── Kebab menu ───────────────────────────────────────── */
    .menu-wrap {
      position: relative;
      flex-shrink: 0;
      align-self: center;
    }
    .menu-btn {
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      color: #6a7585;
      cursor: pointer;
      padding: 0;
      transition: background 0.12s, color 0.12s, border-color 0.12s;
    }
    .menu-btn:hover {
      background: #f3f4f6;
      color: #1a2433;
    }
    .menu-btn:focus-visible {
      outline: 2px solid #0078d4;
      outline-offset: 2px;
    }
    .menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      min-width: 140px;
      padding: 4px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
      display: none;
      flex-direction: column;
      gap: 2px;
      z-index: 20;
    }
    .menu.open {
      display: flex;
    }
    .menu-item {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      font-size: 12.5px;
      color: #1a2433;
      background: transparent;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      text-align: left;
      white-space: nowrap;
    }
    .menu-item:hover {
      background: #f3f4f6;
    }
    .menu-item.destructive {
      color: #b53b3b;
    }
    .menu-item.destructive:hover {
      background: #fceae8;
    }
    .menu-divider {
      height: 1px;
      background: #eef0f3;
      margin: 2px 4px;
    }
  `;
__decorate([
    property({ type: Object })
], OperationCard.prototype, "change", void 0);
__decorate([
    property({ type: String })
], OperationCard.prototype, "name", void 0);
__decorate([
    property({ type: String })
], OperationCard.prototype, "subtitle", void 0);
__decorate([
    property({ type: Object })
], OperationCard.prototype, "network", void 0);
__decorate([
    property({ type: Object })
], OperationCard.prototype, "sourceNetwork", void 0);
__decorate([
    property({ type: Array })
], OperationCard.prototype, "props", void 0);
__decorate([
    property({ type: String, attribute: 'impact-text' })
], OperationCard.prototype, "impactText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-impact' })
], OperationCard.prototype, "hideImpact", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-actions' })
], OperationCard.prototype, "hideActions", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], OperationCard.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], OperationCard.prototype, "selected", void 0);
__decorate([
    state()
], OperationCard.prototype, "_menuOpen", void 0);
OperationCard = __decorate([
    localized(),
    customElement('operation-card')
], OperationCard);
export { OperationCard };
//# sourceMappingURL=OperationCard.js.map